import React, { useEffect, useState, useCallback } from 'react';
import './DocumentViewer.css';

/**
 * Source document viewer.
 *
 * Generic component — works for any tenant whose backend exposes
 * GET /api/document/<filename>?anchor=<idx>&context=<n>.
 * Auto-disables on the backend when the active schema has no text_path column.
 *
 * Props:
 *   filename       (string)   filename returned by the backend (no path)
 *   anchorParaIdx  (number?)  paragraph index to highlight
 *   contextSize    (number?)  paragraphs above/below the anchor (default 2)
 *   onClose        (fn)       called when the user dismisses the viewer
 *   authFetch      (fn)       fetch wrapper that injects the JWT
 */
const DocumentViewer = ({
  filename,
  anchorParaIdx = null,
  contextSize = 2,
  onClose,
  authFetch,
  isFront = true,
  onFocus,
  coexist = false,
}) => {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    if (!filename) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (anchorParaIdx !== null && anchorParaIdx !== undefined) {
      params.set('anchor', String(anchorParaIdx));
    }
    params.set('context', String(showAll ? -1 : contextSize));
    const url = `/api/document/${encodeURIComponent(filename)}?${params.toString()}`;
    authFetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setDoc)
      .catch(e => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  }, [filename, anchorParaIdx, contextSize, showAll, authFetch]);

  useEffect(() => { load(); }, [load]);

  // Escape to close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!filename) return null;

  const metadata = doc?.metadata || {};
  const sourceUrl = doc?.source_url;

  // Pick a sensible header strip. Read commonly present fields if they exist;
  // otherwise just show the filename. Stays generic — no A2A assumptions.
  const headerFields = [
    metadata.acronym,
    metadata.title,
    metadata.frameworkProgramme,
    metadata.start_year || metadata.startDate,
    metadata.doc_topic_label,
  ].filter(Boolean);

  return (
    <div
      className={`document-viewer-overlay${isFront ? ' doc-front' : ''}${coexist ? ' doc-coexist' : ''}`}
      onClick={onClose}
    >
      <div
        className="document-viewer"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={onFocus}
      >
        <div className="document-viewer-header">
          <div className="document-viewer-header-main">
            <div className="document-viewer-title">{metadata.title || filename}</div>
            {headerFields.length > 0 && (
              <div className="document-viewer-meta">
                {headerFields.map((f, i) => (
                  <span key={i} className="document-viewer-meta-chip">{String(f)}</span>
                ))}
              </div>
            )}
          </div>
          <div className="document-viewer-actions">
            {sourceUrl && (
              <a
                className="document-viewer-source-btn"
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Get source ↗
              </a>
            )}
            <button className="document-viewer-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        <div className="document-viewer-body">
          {loading && <div className="document-viewer-loading">Loading…</div>}
          {error && <div className="document-viewer-error">Could not load document: {error}</div>}
          {!loading && !error && doc && (
            <>
              <div className="document-viewer-context-bar">
                <span className="document-viewer-context-info">
                  {doc.paragraphs.length} of {doc.total_paragraphs} paragraphs
                  {anchorParaIdx !== null && ` · anchor at #${anchorParaIdx}`}
                </span>
                {doc.total_paragraphs > doc.paragraphs.length && !showAll && (
                  <button className="document-viewer-show-all" onClick={() => setShowAll(true)}>
                    Show whole document
                  </button>
                )}
                {showAll && (
                  <button className="document-viewer-show-all" onClick={() => setShowAll(false)}>
                    Collapse to anchor ± {contextSize}
                  </button>
                )}
              </div>

              <ol className="document-viewer-paragraphs">
                {doc.paragraphs.map(p => (
                  <li
                    key={p.idx}
                    className={`document-viewer-paragraph${p.is_anchor ? ' document-viewer-paragraph--anchor' : ''}`}
                    value={p.idx + 1}
                  >
                    {p.text}
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
